import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, UserPlus, Users } from 'lucide-react';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { DochazkaZaznam, OsobaNaStavbe } from '../../api/types.js';
import type { TerenKontext } from '../../components/TerenLayout.js';
import { Btn, Chyba, Input, Nacitani, VelkeTlacitko } from '../../components/ui.js';
import { dnesniDen } from './datum.js';

/**
 * Docházka party (terén) — mistr zapíše den ZA PARTU pár ťuknutími: kdo tu
 * byl (přepínač) a kolik hodin (výchozí 8). Dělník appku nedrží — je to Osoba
 * na stavbě bez loginu. Uložení NAHRADÍ den (obrazovka = pravda dne). Hodiny
 * jdou do Excelu pro mzdový systém, žádné mzdy tady.
 */

interface RadekStav {
  pritomen: boolean;
  hodiny: string;
}

export function DochazkaPage() {
  const { zakazka } = useOutletContext<TerenKontext>();
  const queryClient = useQueryClient();
  const [den, setDen] = useState(dnesniDen());
  const [radky, setRadky] = useState<Record<string, RadekStav>>({});
  const [pridavam, setPridavam] = useState(false);
  const [ulozeno, setUlozeno] = useState(false);

  const { data: osoby, isLoading: nacitamOsoby } = useQuery({
    queryKey: ['dochazka-osoby'],
    queryFn: () => api.get<OsobaNaStavbe[]>('/api/dochazka/osoby'),
  });

  const { data: zapsane } = useQuery({
    queryKey: ['dochazka', zakazka.id, den],
    queryFn: () =>
      api.get<DochazkaZaznam[]>(`/api/dochazka?zakazkaId=${zakazka.id}&od=${den}&do=${den}`),
  });

  // Předvyplnění z uloženého dne — mistr vidí, co už zapsal.
  useEffect(() => {
    if (!zapsane) return;
    setRadky(
      Object.fromEntries(
        zapsane.map((z) => [z.osoba.id, { pritomen: true, hodiny: z.odpracovanoHodin ?? '8' }]),
      ),
    );
  }, [zapsane]);

  // Potvrzení „uloženo" platí pro právě zapsaný den — změna dne ho maže.
  useEffect(() => setUlozeno(false), [den, zakazka.id]);

  const aktivni = useMemo(() => (osoby ?? []).filter((o) => o.aktivni), [osoby]);
  const pritomnych = aktivni.filter((o) => radky[o.id]?.pritomen).length;

  const uloz = useMutation({
    mutationFn: () =>
      api.post('/api/dochazka/den', {
        zakazkaId: zakazka.id,
        datum: den,
        zaznamy: aktivni
          .filter((o) => radky[o.id]?.pritomen)
          .map((o) => ({
            osobaId: o.id,
            odpracovanoHodin: Number(radky[o.id]?.hodiny ?? '8') || 0,
          })),
      }),
    onSuccess: () => {
      setUlozeno(true);
      void queryClient.invalidateQueries({ queryKey: ['dochazka', zakazka.id, den] });
    },
  });

  const prepni = (id: string) =>
    setRadky((s) => ({
      ...s,
      [id]: { pritomen: !s[id]?.pritomen, hodiny: s[id]?.hodiny ?? '8' },
    }));

  const vsichni8 = () =>
    setRadky(Object.fromEntries(aktivni.map((o) => [o.id, { pritomen: true, hodiny: '8' }])));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Users size={22} strokeWidth={1.75} aria-hidden="true" className="text-primary" />
        <h1 className="text-xl font-bold">Docházka party</h1>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-base font-semibold">Den</span>
        <Input
          type="date"
          aria-label="Den docházky"
          value={den}
          onChange={(e) => setDen(e.target.value)}
        />
      </label>

      {nacitamOsoby && <Nacitani text="Načítám partu…" />}

      {aktivni.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-base text-text-secondary">
              Na stavbě: <span className="font-bold text-text-primary">{pritomnych}</span> z{' '}
              {aktivni.length}
            </p>
            <Btn size="teren" varianta="secondary" onClick={vsichni8}>
              Všichni 8 h
            </Btn>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-surface">
            {aktivni.map((o) => {
              const stav = radky[o.id];
              return (
                <div
                  key={o.id}
                  className="flex min-h-14 items-center gap-3 border-t border-border px-4 py-2 first:border-t-0"
                >
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!stav?.pritomen}
                    aria-label={`${o.jmeno} — na stavbě`}
                    onClick={() => prepni(o.id)}
                    className={`flex min-h-12 min-w-0 flex-1 items-center gap-3 text-left ${
                      stav?.pritomen ? '' : 'opacity-55'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 ${
                        stav?.pritomen
                          ? 'border-success bg-success-bg text-success-text'
                          : 'border-border-strong bg-surface'
                      }`}
                    >
                      {stav?.pritomen && <CheckCircle2 size={16} strokeWidth={2.5} />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-base font-semibold">{o.jmeno}</span>
                      <span className="block truncate text-sm text-text-secondary">
                        {o.profese}
                        {o.subdodavatel ? ` · ${o.subdodavatel.nazev}` : ''}
                      </span>
                    </span>
                  </button>
                  {stav?.pritomen && (
                    <label className="flex shrink-0 items-center gap-1 text-sm text-text-secondary">
                      <Input
                        inputMode="decimal"
                        aria-label={`${o.jmeno} — hodin`}
                        className="w-16 text-center text-base font-semibold"
                        value={stav.hodiny}
                        onChange={(e) =>
                          setRadky((s) => ({
                            ...s,
                            [o.id]: { pritomen: true, hodiny: e.target.value },
                          }))
                        }
                      />
                      h
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {osoby && aktivni.length === 0 && (
        <p className="rounded-xl border border-dashed border-border-strong bg-surface p-4 text-base text-text-secondary">
          Zatím tu nikoho nemáte — přidejte lidi z party níže. Zapisujete je jednou, pak už jen
          ťukáte docházku.
        </p>
      )}

      {pridavam ? (
        <PridatOsobu
          onHotovo={(nova) => {
            setPridavam(false);
            if (nova) {
              void queryClient.invalidateQueries({ queryKey: ['dochazka-osoby'] });
              setRadky((s) => ({ ...s, [nova.id]: { pritomen: true, hodiny: '8' } }));
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="flex items-center gap-2 self-start text-base font-semibold text-primary underline underline-offset-2"
          onClick={() => setPridavam(true)}
        >
          <UserPlus size={18} strokeWidth={2} aria-hidden="true" /> Přidat člověka do party
        </button>
      )}

      {uloz.error ? <Chyba>{hlaskaZChyby(uloz.error)}</Chyba> : null}
      {ulozeno && !uloz.isPending && (
        <p className="flex items-center gap-1.5 text-base font-semibold text-success-text">
          <CheckCircle2 size={18} strokeWidth={2} aria-hidden="true" /> Docházka dne je uložená.
        </p>
      )}

      <VelkeTlacitko
        onClick={() => uloz.mutate()}
        disabled={uloz.isPending || aktivni.length === 0}
      >
        {uloz.isPending ? 'Ukládám…' : `Uložit den (${pritomnych} osob)`}
      </VelkeTlacitko>
    </div>
  );
}

/** Inline založení Osoby na stavbě — jméno + profese stačí. */
function PridatOsobu({ onHotovo }: { onHotovo: (nova: OsobaNaStavbe | null) => void }) {
  const [jmeno, setJmeno] = useState('');
  const [profese, setProfese] = useState('');
  const uloz = useMutation({
    mutationFn: () => api.post<OsobaNaStavbe>('/api/dochazka/osoby', { jmeno, profese }),
    onSuccess: (nova) => onHotovo(nova),
  });
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4">
      <p className="text-base font-semibold">Nový člověk do party</p>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-text-secondary">Jméno a příjmení</span>
        <Input value={jmeno} onChange={(e) => setJmeno(e.target.value)} autoFocus />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-text-secondary">Profese (např. zedník)</span>
        <Input value={profese} onChange={(e) => setProfese(e.target.value)} />
      </label>
      {uloz.error ? <Chyba>{hlaskaZChyby(uloz.error)}</Chyba> : null}
      <div className="flex gap-2">
        <Btn
          size="teren"
          onClick={() => uloz.mutate()}
          disabled={uloz.isPending || !jmeno.trim() || !profese.trim()}
        >
          Přidat
        </Btn>
        <Btn size="teren" varianta="secondary" onClick={() => onHotovo(null)}>
          Zrušit
        </Btn>
      </div>
    </div>
  );
}
